import React, { useContext, useState, useEffect } from 'react'
import {
  Container,
  TextField,
  Select,
  MenuItem,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Pagination,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Stack,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import SearchIcon from '@mui/icons-material/Search'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import SyncIcon from '@mui/icons-material/Sync'
import BadgeIcon from '@mui/icons-material/Badge'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AccountContext } from 'contexts/AccountContext'
import TableLoading from 'components/TableLoading'
import SyncDialog from 'components/SyncDialog'
import { useAccountSync } from 'hooks/useAccountSync'
import { updateNetsuiteIds, UpdateNetsuiteIdsResponse } from 'api/netsuite'

const AccountManagementPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')

  const {
    accounts,
    isLoading,
    error,
    fetchAccounts,
    totalPages,
    currentPage,
    status,
    setStatus
  } = useContext(AccountContext)

  // Account sync state
  const {
    isLoading: isAccountSyncing,
    syncResult: accountSyncResult,
    updatedCount: accountUpdatedCount,
    syncAccounts
  } = useAccountSync()
  const [accountDialogOpen, setAccountDialogOpen] = useState(false)
  const [accountError, setAccountError] = useState<string | null>(null)

  // Update NetSuite IDs state
  const [isUpdatingIds, setIsUpdatingIds] = useState(false)
  const [idsDialogOpen, setIdsDialogOpen] = useState(false)
  const [idsError, setIdsError] = useState<string | null>(null)
  const [idsResult, setIdsResult] = useState<UpdateNetsuiteIdsResponse | null>(
    null
  )

  useEffect(() => {
    const statusParam = searchParams.get('status')
    if (
      statusParam &&
      (statusParam === 'Approved' || statusParam === 'Pending')
    ) {
      setStatus(statusParam)
      fetchAccounts('', statusParam, 1)
    }
  }, [])

  const handleSearch = () => {
    fetchAccounts(email, status, 1)
  }

  const handlePageChange = (_: any, newPage: number) => {
    fetchAccounts(email, status, newPage)
  }

  const handleStatusChange = (e: SelectChangeEvent) => {
    const newStatus = e.target.value
    setStatus(newStatus)
    fetchAccounts(email, newStatus, 1)

    if (newStatus) {
      navigate(`/accounts?status=${newStatus}`, { replace: true })
    } else {
      navigate('/accounts', { replace: true })
    }
  }

  const handleSyncAccounts = async () => {
    setAccountError(null)
    setAccountDialogOpen(true)
    try {
      await syncAccounts()
    } catch (err: any) {
      setAccountError(err.message || 'Failed to sync accounts')
    }
  }

  const handleUpdateNetsuiteIds = async () => {
    setIdsError(null)
    setIdsDialogOpen(true)
    setIsUpdatingIds(true)
    setIdsResult(null)
    try {
      const result = await updateNetsuiteIds()
      setIdsResult(result)
    } catch (err: any) {
      setIdsError(
        err.response?.data?.error ||
          err.message ||
          'Failed to update NetSuite customer IDs'
      )
    } finally {
      setIsUpdatingIds(false)
    }
  }

  return (
    <Container maxWidth='xl' sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap'
          }}
        >
          <Stack direction='row' alignItems='center' spacing={2}>
            <PeopleAltIcon fontSize='large' color='primary' />
            <Typography variant='h4'>Manage Accounts</Typography>
          </Stack>
          <Stack direction='row' spacing={1}>
            <Tooltip title='Sync discount rates, tax status, and other account fields from NetSuite.'>
              <span>
                <Button
                  variant='outlined'
                  startIcon={<SyncIcon fontSize='small' />}
                  onClick={handleSyncAccounts}
                  disabled={isAccountSyncing}
                >
                  Sync from NetSuite
                </Button>
              </span>
            </Tooltip>
            <Tooltip title='Verify and fix invalid or missing NetSuite customer IDs for all accounts.'>
              <span>
                <Button
                  variant='outlined'
                  startIcon={<BadgeIcon fontSize='small' />}
                  onClick={handleUpdateNetsuiteIds}
                  disabled={isUpdatingIds}
                >
                  Update customer IDs
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        <Box
          sx={{
            mb: 3,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              sx={{ minWidth: '300px' }}
              label='Search by Email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
              InputProps={{
                endAdornment: (
                  <Button
                    sx={{ minWidth: 'auto', p: 1 }}
                    onClick={handleSearch}
                  >
                    <SearchIcon />
                  </Button>
                )
              }}
            />

            <FormControl sx={{ minWidth: '200px' }}>
              <InputLabel id='status-label'>Status</InputLabel>
              <Select
                labelId='status-label'
                id='status'
                value={status}
                label='Status'
                onChange={handleStatusChange}
              >
                <MenuItem value=''>
                  <em>All Status</em>
                </MenuItem>
                <MenuItem value='Approved'>Approved</MenuItem>
                <MenuItem value='Pending'>Pending</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {error && <Typography color='error'>{error}</Typography>}

        {isLoading ? (
          <TableLoading />
        ) : (
          <Grid container spacing={2}>
            {accounts.map(account => (
              <Grid item xs={12} md={6} key={account.account?.id}>
                <Card variant='outlined'>
                  <CardContent>
                    <Typography>
                      <strong>Email:</strong>{' '}
                      {'email' in account.account ? account.account.email : ''}
                    </Typography>
                    <Box display='flex' alignItems='center'>
                      <Typography fontWeight='bold' sx={{ mr: 1 }}>
                        Status:
                      </Typography>
                      {account.accountStatus === 'Approved' ? (
                        <Box display='flex' alignItems='center' color='green'>
                          <CheckCircleIcon fontSize='small' sx={{ mr: 0.5 }} />
                          <Typography color='green'>Approved</Typography>
                        </Box>
                      ) : (
                        <Box display='flex' alignItems='center' color='orange'>
                          <CancelIcon fontSize='small' sx={{ mr: 0.5 }} />
                          <Typography color='orange'>Pending</Typography>
                        </Box>
                      )}
                    </Box>
                    <Button
                      variant='outlined'
                      size='small'
                      sx={{ mt: 1 }}
                      onClick={() =>
                        navigate(`/accounts/${account.account!.id}`)
                      }
                    >
                      View Account Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {totalPages > 1 && (
          <Box display='flex' justifyContent='center' mt={4}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color='primary'
            />
          </Box>
        )}
      </Paper>

      <SyncDialog
        open={accountDialogOpen}
        onClose={() => setAccountDialogOpen(false)}
        title='Sync Accounts from NetSuite'
        isLoading={isAccountSyncing}
        error={accountError}
        syncResult={accountSyncResult}
        updatedCount={accountUpdatedCount}
        resultType='account'
      />

      <Dialog
        open={idsDialogOpen}
        onClose={!isUpdatingIds ? () => setIdsDialogOpen(false) : undefined}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Update NetSuite Customer IDs</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {isUpdatingIds && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 4
                }}
              >
                <CircularProgress size={60} />
                <Typography variant='body1' sx={{ mt: 2 }}>
                  Updating NetSuite customer IDs, please wait...
                </Typography>
              </Box>
            )}

            {idsError && (
              <Alert severity='error' icon={<ErrorIcon />}>
                {idsError}
              </Alert>
            )}

            {!isUpdatingIds && idsResult && idsResult.updatedCount > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon color='success' fontSize='large' />
                  <Box>
                    <Typography variant='h6'>Update Complete</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {idsResult.updatedCount} customer ID
                      {idsResult.updatedCount !== 1 ? 's' : ''} updated
                      successfully
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Email</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Old ID</strong>
                        </TableCell>
                        <TableCell>
                          <strong>New ID</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {idsResult.updates.map((item, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{item.email}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.oldNetsuiteCustomerId}
                              size='small'
                              color='default'
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.newNetsuiteCustomerId}
                              size='small'
                              color='success'
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {idsResult.notFoundCount > 0 && (
                  <Alert severity='warning'>
                    {idsResult.notFoundCount} account
                    {idsResult.notFoundCount !== 1 ? 's' : ''} not found in
                    NetSuite:
                    <Box component='ul' sx={{ m: 0, mt: 1, pl: 2 }}>
                      {idsResult.notFound.map((email, idx) => (
                        <li key={idx}>{email}</li>
                      ))}
                    </Box>
                  </Alert>
                )}
              </>
            )}

            {!isUpdatingIds && idsResult && idsResult.updatedCount === 0 && (
              <Alert severity='info' icon={<InfoIcon />}>
                {idsResult.message ||
                  'All NetSuite customer IDs are valid. No updates needed.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIdsDialogOpen(false)}
            disabled={isUpdatingIds}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default AccountManagementPage
